// PATH: src/pages/paiement/ReglementEcole.tsx
//
// Ce que l'établissement doit nous régler.
//
// Les élèves sont DÉJÀ premium : leur accès ne dépend pas de ce paiement. Cet
// écran ne traite que la dette de l'école envers nous — d'où le vocabulaire
// « régler » plutôt que « payer pour activer ».
//
// Une ligne par élève, jamais refacturée une fois payée. C'est ce qui résout le
// cas des inscriptions échelonnées : une école qui paie trois élèves un jour et
// en inscrit deux le lendemain ne voit que les deux nouveaux.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Divider, Empty, Radio, Space, Spin, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RadioChangeEvent } from 'antd';
import { BankOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePaiementApi } from '../../api/paiement/paiement';
import type {
    TEtatReglement,
    TSiegeARegler,
    TSiegeRegle,
    TEcheanceAbonnement,
    Periodicite,
} from '../../api/paiement/paiement';

const money = (v: number, devise: string) =>
    `${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${devise || ''}`;

const dateCourte = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '');

type Props = {
    /**
     * Remonte l'etat de reglement au parent, qui s'en sert pour annoter la
     * liste des comptes. Un second appel depuis le tableau aurait donne deux
     * verites possibles sur ce qui est paye ; il n'y en a qu'une.
     */
    onEtat?: (etat: TEtatReglement | null) => void;
};

const ReglementEcole: React.FC<Props> = ({ onEtat }) => {
    const { t } = useTranslation();
    const paiementApi = usePaiementApi();

    const [etat, setEtat] = useState<TEtatReglement | null>(null);
    const [chargement, setChargement] = useState(true);
    const [selection, setSelection] = useState<React.Key[]>([]);
    const [vendor, setVendor] = useState<string>('cashplus');
    const [periodicite, setPeriodicite] = useState<Periodicite>('mensuelle');
    const [envoi, setEnvoi] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    // L'abonnement se regle separement des sieges : deux dettes, deux
    // selections, deux paiements. Les melanger empecherait de savoir laquelle
    // solder a l'encaissement.
    const [selAbo, setSelAbo] = useState<React.Key[]>([]);
    const [envoiAbo, setEnvoiAbo] = useState(false);
    const [vendorAbo, setVendorAbo] = useState<string>('cashplus');
    const [tokenAbo, setTokenAbo] = useState<string | null>(null);

    // onEtat change d'identite a chaque rendu du parent : le garder dans une ref
    // evite de relancer le chargement en boucle.
    const onEtatRef = React.useRef(onEtat);
    onEtatRef.current = onEtat;

    const charger = useCallback(async () => {
        setChargement(true);
        try {
            const data = await paiementApi.etatReglement();
            setEtat(data);
            onEtatRef.current?.(data);
            // Tout est coché par défaut : le cas courant est « je règle tout ».
            // L'école peut décocher, mais n'a rien à faire pour le cas normal.
            setSelection((data.a_regler ?? []).map((x) => x.id_billing));
            setSelAbo((data.abonnements ?? []).map((x) => x.id));
        } catch {
            message.error(t('reglement.loadError', 'Impossible de charger votre situation de règlement.'));
        } finally {
            setChargement(false);
        }
        // paiementApi change d'identité à chaque rendu : l'inclure boucle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    useEffect(() => {
        charger();
    }, [charger]);

    // Memoise : sans cela, le tableau change d'identite a chaque rendu et
    // relance le calcul du total en boucle.
    const aRegler = useMemo(() => etat?.a_regler ?? [], [etat]);
    const devise = etat?.devise || 'MAD';

    // Par CARTE, l'abonnement se reconduit au rythme choisi : chaque échéance
    // couvre autant de mois, et le montant suit. Les lignes, elles, ne portent
    // qu'un mois — c'est exactement le facteur qu'applique le serveur.
    //
    // Sans lui, l'écran annonçait 405 MAD là où le paiement en demandait 1 215 :
    // l'école aurait découvert le vrai montant sur la page du prestataire.
    const moisParEcheance =
        vendor !== 'lemonsqueezy'
            ? 1
            : periodicite === 'annuelle'
              ? 12
              : periodicite === 'trimestrielle'
                ? 3
                : 1;

    // Le total suit la sélection, pour que l'école voie exactement ce qu'elle
    // s'apprête à payer — pas un montant global qui ne correspondrait plus.
    const totalSelection = useMemo(() => {
        const choisis = new Set(selection.map(Number));
        const base = aRegler.filter((x) => choisis.has(x.id_billing)).reduce((s, x) => s + x.montant, 0);
        return base * moisParEcheance;
    }, [selection, aRegler, moisParEcheance]);

    const regler = async () => {
        if (selection.length === 0) return;
        setEnvoi(true);
        setToken(null);
        try {
            const resp = await paiementApi.reglerSieges(
                selection.map(Number),
                vendor,
                vendor === 'lemonsqueezy' ? periodicite : undefined
            );
            if (resp.checkout_url) {
                // Redirection plutôt qu'un nouvel onglet : un popup bloqué
                // laisserait l'école sans page de paiement ni explication.
                window.location.assign(resp.checkout_url);
                return;
            }
            if (resp.cashplus_token) {
                setToken(resp.cashplus_token);
                message.success(t('reglement.tokenReady', 'Votre code de paiement est prêt.'));
                return;
            }
            // Virement : rien à faire en ligne.
            message.success(
                t('reglement.virementOk', 'Montant à virer : {{montant}}. Nous confirmerons dès réception.', {
                    montant: money(resp.total, resp.devise),
                })
            );
            await charger();
        } catch (err: any) {
            message.error(err?.response?.data?.error || t('reglement.payError', 'Le règlement n’a pas pu être lancé.'));
        } finally {
            setEnvoi(false);
        }
    };

    const abonnements = useMemo(() => etat?.abonnements ?? [], [etat]);

    const totalAbo = useMemo(() => {
        const choisis = new Set(selAbo.map(Number));
        return abonnements.filter((x) => choisis.has(x.id)).reduce((s, x) => s + x.montant, 0);
    }, [selAbo, abonnements]);

    const reglerAbo = async () => {
        if (selAbo.length === 0) return;
        setEnvoiAbo(true);
        setTokenAbo(null);
        try {
            const resp = await paiementApi.reglerAbonnement(selAbo.map(Number), vendorAbo);
            if (resp.checkout_url) {
                window.location.assign(resp.checkout_url);
                return;
            }
            if (resp.cashplus_token) {
                setTokenAbo(resp.cashplus_token);
                message.success(t('reglement.tokenReady', 'Votre code de paiement est prêt.'));
                return;
            }
            message.success(
                t('reglement.virementOk', 'Montant à virer : {{montant}}. Nous confirmerons dès réception.', {
                    montant: money(resp.total, resp.devise),
                })
            );
            await charger();
        } catch (err: any) {
            message.error(err?.response?.data?.error || t('reglement.payError', 'Le règlement n’a pas pu être lancé.'));
        } finally {
            setEnvoiAbo(false);
        }
    };

    const moisLong = (iso: string) =>
        new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });

    const colonnesAbo: ColumnsType<TEcheanceAbonnement> = [
        {
            title: t('reglement.period', 'Période'),
            key: 'periode',
            render: (_, r) => moisLong(r.periode),
        },
        {
            // Le detail du calcul evite d'avoir a nous demander « pourquoi ce
            // montant ? » : l'effectif facture est celui du jour de la
            // generation, et il est fige.
            title: t('reglement.basis', 'Base de calcul'),
            key: 'base',
            render: (_, r) => (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('reglement.basisDetail', '{{n}} élève(s) × {{prix}}', {
                        n: r.quantite,
                        prix: money(r.prix_unitaire, r.devise),
                    })}
                </Typography.Text>
            ),
        },
        {
            title: t('reglement.amount', 'Montant'),
            key: 'montant',
            width: 130,
            align: 'right',
            render: (_, r) => money(r.montant, r.devise),
        },
    ];

    const colonnesDues: ColumnsType<TSiegeARegler> = [
        { title: t('reglement.student', 'Élève'), dataIndex: 'nom', key: 'nom' },
        {
            title: t('reglement.period', 'Période'),
            key: 'periode',
            width: 200,
            render: (_, r) => `${dateCourte(r.debut)} → ${dateCourte(r.fin)}`,
        },
        {
            title: t('reglement.amount', 'Montant'),
            key: 'montant',
            width: 130,
            align: 'right',
            render: (_, r) => money(r.montant, r.devise),
        },
    ];

    const colonnesReglees: ColumnsType<TSiegeRegle> = [
        { title: t('reglement.student', 'Élève'), dataIndex: 'nom', key: 'nom' },
        {
            title: t('reglement.paidOn', 'Réglé le'),
            key: 'regle',
            width: 200,
            render: (_, r) => (
                <Space size={6}>
                    <span>{dateCourte(r.regle_le)}</span>
                    {r.vendor && <Tag>{r.vendor}</Tag>}
                </Space>
            ),
        },
        {
            title: t('reglement.amount', 'Montant'),
            key: 'montant',
            width: 130,
            align: 'right',
            render: (_, r) => money(r.montant, r.devise),
        },
    ];

    if (chargement) {
        return (
            <Card size="small" style={{ marginTop: 16 }}>
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                </div>
            </Card>
        );
    }

    const enCours = etat?.en_cours;

    return (
        <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 16 }}>
            {/* Un règlement lancé retire ses lignes de la liste des dus : sans ce
                rappel, l'école verrait sa liste se vider et croirait avoir payé.
                C'est aussi ce qui lui redonne son code CashPlus si elle l'a perdu. */}
            {enCours && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('reglement.pendingTitle', 'Un règlement est en attente de paiement')}
                    description={
                        <Space direction="vertical" size={4}>
                            <Typography.Text>
                                {t(
                                    'reglement.pendingBody',
                                    '{{montant}} pour {{lignes}} élève(s). Ces élèves ne sont plus proposés ci-dessous tant que ce paiement n’est pas encaissé.',
                                    {
                                        montant: money(enCours.total, enCours.devise || devise),
                                        lignes: enCours.lignes,
                                    }
                                )}
                            </Typography.Text>
                            {enCours.cashplus_token && (
                                <Typography.Text>
                                    {t('reglement.pendingToken', 'Code CashPlus :')}{' '}
                                    <Typography.Text code style={{ fontSize: 15 }}>
                                        {enCours.cashplus_token}
                                    </Typography.Text>
                                </Typography.Text>
                            )}
                            {enCours.checkout_url && (
                                <Button
                                    size="small"
                                    type="primary"
                                    onClick={() => window.location.assign(enCours.checkout_url!)}
                                >
                                    {t('reglement.pendingResume', 'Reprendre le paiement')}
                                </Button>
                            )}
                            {enCours.expire && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {t(
                                        'reglement.pendingExpiry',
                                        'Sans paiement, ces élèves redeviendront réglables après le {{date}}.',
                                        { date: new Date(enCours.expire).toLocaleString() }
                                    )}
                                </Typography.Text>
                            )}
                        </Space>
                    }
                />
            )}
            {/* Le code que l'établissement remet aux familles.
                Il n'apparaissait nulle part côté école, alors que c'est elle
                qui doit le transmettre — et le retrouver quand elle l'a
                perdu. Sa place est ici : c'est l'écran qu'elle ouvre quand
                elle s'occupe des abonnements de ses élèves. */}
            {etat?.code_ecole && (
                <Card size="small" style={{ marginBottom: 12 }}>
                    <Space direction="vertical" size={2}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {t('reglement.schoolCode', 'Code à remettre aux familles')}
                        </Typography.Text>
                        <Space>
                            <Typography.Text strong copyable style={{ fontSize: 16, letterSpacing: 1 }}>
                                {etat.code_ecole}
                            </Typography.Text>
                        </Space>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {t(
                                'reglement.schoolCodeHint',
                                "Les parents le saisissent au paiement : le tarif négocié avec vous remplace le tarif public."
                            )}
                        </Typography.Text>
                    </Space>
                </Card>
            )}
            <Card
                size="small"
                title={t('reglement.seatsTitle', 'Sièges élèves')}
                extra={
                    aRegler.length > 0 ? (
                        <Typography.Text strong style={{ fontSize: 15 }}>
                            {money((etat?.total_du ?? 0) * moisParEcheance, devise)}
                        </Typography.Text>
                    ) : null
                }
            >
                {aRegler.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            enCours
                                ? t(
                                      'reglement.nothingDuePending',
                                      'Rien de plus à régler : votre paiement en attente couvre tous vos élèves.'
                                  )
                                : abonnements.length > 0
                                  ? // « À jour » serait faux s'il reste l'abonnement à payer.
                                    t(
                                        'reglement.nothingDueSeats',
                                        'Aucun siège élève à régler. Votre abonnement TilmiSchool reste à payer ci-dessous.'
                                    )
                                  : t('reglement.nothingDue', 'Vous êtes à jour, rien à régler.')
                        }
                    />
                ) : (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={t('reglement.alreadyPremium', 'Ces élèves sont déjà premium')}
                            description={t(
                                'reglement.alreadyPremiumHint',
                                "Leur accès ne dépend pas de ce règlement : il concerne uniquement ce que votre établissement nous doit."
                            )}
                        />
                        <Table<TSiegeARegler>
                            size="small"
                            rowKey="id_billing"
                            dataSource={aRegler}
                            columns={colonnesDues}
                            pagination={false}
                            rowSelection={{
                                selectedRowKeys: selection,
                                onChange: setSelection,
                            }}
                        />

                        <Divider style={{ margin: '16px 0 12px' }} />

                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div>
                                <Typography.Text style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                                    {t('reglement.method', 'Moyen de règlement')}
                                </Typography.Text>
                                <Radio.Group
                                    value={vendor}
                                    onChange={(e: RadioChangeEvent) => setVendor(e.target.value)}
                                    optionType="button"
                                    buttonStyle="solid"
                                    size="small"
                                    options={[
                                        { label: 'CashPlus', value: 'cashplus' },
                                        { label: t('reglement.card', 'Carte bancaire'), value: 'lemonsqueezy' },
                                        { label: t('reglement.transfer', 'Virement'), value: 'virement' },
                                    ]}
                                />
                            </div>

                            {/* La carte ouvre un abonnement reconductible : on choisit un
                                rythme, pas une durée. */}
                            {vendor === 'lemonsqueezy' && (
                                <div>
                                    <Typography.Text style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                                        {t('reglement.rhythm', 'Rythme de prélèvement')}
                                    </Typography.Text>
                                    <Radio.Group
                                        value={periodicite}
                                        onChange={(e: RadioChangeEvent) => setPeriodicite(e.target.value)}
                                        optionType="button"
                                        buttonStyle="solid"
                                        size="small"
                                        options={[
                                            { label: t('reglement.monthly', 'Mensuel'), value: 'mensuelle' },
                                            { label: t('reglement.quarterly', 'Trimestriel'), value: 'trimestrielle' },
                                            { label: t('reglement.yearly', 'Annuel'), value: 'annuelle' },
                                        ]}
                                    />
                                </div>
                            )}

                            {moisParEcheance > 1 && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {t(
                                        'reglement.termCovers',
                                        'Chaque échéance couvre {{mois}} mois et sera prélevée automatiquement.',
                                        { mois: moisParEcheance }
                                    )}
                                </Typography.Text>
                            )}

                            <Button
                                type="primary"
                                icon={vendor === 'lemonsqueezy' ? <CreditCardOutlined /> : <BankOutlined />}
                                loading={envoi}
                                disabled={selection.length === 0}
                                onClick={regler}
                            >
                                {t('reglement.pay', 'Régler {{montant}}', {
                                    montant: money(totalSelection, devise),
                                })}
                            </Button>

                            {token && (
                                <Alert
                                    type="success"
                                    showIcon
                                    message={t('reglement.cashplusTitle', 'Code CashPlus')}
                                    description={
                                        <Space direction="vertical" size={4}>
                                            <Typography.Text code style={{ fontSize: 18 }}>
                                                {token}
                                            </Typography.Text>
                                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                {t(
                                                    'reglement.cashplusHint',
                                                    'À présenter dans une agence CashPlus. Votre règlement sera confirmé automatiquement.'
                                                )}
                                            </Typography.Text>
                                        </Space>
                                    }
                                />
                            )}
                        </Space>
                    </>
                )}
            </Card>

            {/* TilmiSchool : dette d'ETABLISSEMENT, assise sur l'effectif et non
                sur des comptes premium. Meme ecran que les sieges, section a
                part — deux dettes qui ne se calculent ni ne se soldent pareil. */}
            {abonnements.length > 0 && (
                <Card
                    size="small"
                    title={t('reglement.subscriptionTitle', 'Votre abonnement TilmiSchool')}
                    extra={
                        <Typography.Text strong style={{ fontSize: 15 }}>
                            {money(etat?.total_abonnements ?? 0, devise)}
                        </Typography.Text>
                    }
                >
                    <Table<TEcheanceAbonnement>
                        size="small"
                        rowKey="id"
                        dataSource={abonnements}
                        columns={colonnesAbo}
                        pagination={false}
                        rowSelection={{ selectedRowKeys: selAbo, onChange: setSelAbo }}
                    />

                    <Divider style={{ margin: '16px 0 12px' }} />

                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <div>
                            <Typography.Text style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                                {t('reglement.method', 'Moyen de règlement')}
                            </Typography.Text>
                            {/* Pas de rythme a choisir ici : un abonnement se
                                regle au mois, et chaque ligne porte deja le mois
                                qu'elle couvre. */}
                            <Radio.Group
                                value={vendorAbo}
                                onChange={(e: RadioChangeEvent) => setVendorAbo(e.target.value)}
                                optionType="button"
                                buttonStyle="solid"
                                size="small"
                                options={[
                                    { label: 'CashPlus', value: 'cashplus' },
                                    { label: t('reglement.card', 'Carte bancaire'), value: 'lemonsqueezy' },
                                    { label: t('reglement.transfer', 'Virement'), value: 'virement' },
                                ]}
                            />
                        </div>

                        <Button
                            type="primary"
                            icon={vendorAbo === 'lemonsqueezy' ? <CreditCardOutlined /> : <BankOutlined />}
                            loading={envoiAbo}
                            disabled={selAbo.length === 0}
                            onClick={reglerAbo}
                        >
                            {t('reglement.pay', 'Régler {{montant}}', { montant: money(totalAbo, devise) })}
                        </Button>

                        {tokenAbo && (
                            <Alert
                                type="success"
                                showIcon
                                message={t('reglement.cashplusTitle', 'Code CashPlus')}
                                description={
                                    <Space direction="vertical" size={4}>
                                        <Typography.Text code style={{ fontSize: 18 }}>
                                            {tokenAbo}
                                        </Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {t(
                                                'reglement.cashplusHint',
                                                'À présenter dans une agence CashPlus. Votre règlement sera confirmé automatiquement.'
                                            )}
                                        </Typography.Text>
                                    </Space>
                                }
                            />
                        )}
                    </Space>
                </Card>
            )}

            {(etat?.regles?.length ?? 0) > 0 && (
                <Card size="small" title={t('reglement.history', 'Déjà réglé')}>
                    <Table<TSiegeRegle>
                        size="small"
                        rowKey="id_billing"
                        dataSource={etat?.regles ?? []}
                        columns={colonnesReglees}
                        pagination={false}
                    />
                </Card>
            )}
        </Space>
    );
};

export default ReglementEcole;
